import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiPhone, FiTrendingUp, FiXCircle,
  FiDollarSign, FiClock, FiAlertCircle, FiArrowRight,
  FiShield, FiBarChart2, FiActivity, FiCalendar,
  FiUserCheck, FiRefreshCw, FiChevronRight, FiEye,
  FiFileText, FiCheckCircle, FiTarget, FiAward,
  FiArrowUpRight, FiArrowDownRight, FiZap,
} from 'react-icons/fi';
import { api } from '@crm/shared';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as CJTooltip, Legend } from 'chart.js';
import { can } from '../../utils/permission';
import authService from '../../services/authService';
import Layout from '../../components/Layout';
import { fmtDate } from '../../utils/dateUtils';
import DateInput from '../../components/DateInput';

ChartJS.register(CategoryScale, LinearScale, BarElement, CJTooltip, Legend);

const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyL:'#EBF4FF', navyM:'#1A5276',
  gold:'#E8930A', goldL:'#FEF3DC', goldB:'#F5A623',
  green:'#15803D', greenL:'#DCFCE7', greenB:'#16A34A',
  red:'#B91C1C', redL:'#FEF2F2', redB:'#DC2626', redBord:'#FECACA',
  sky:'#0369A1', skyL:'#DBEAFE', skyB:'#0284C7',
  purple:'#6D28D9', purpleL:'#EDE9FE', purpleB:'#7C3AED',
  orange:'#C2410C', orangeL:'#FFF7ED',
  border:'#E5E7EB', borderL:'#F3F4F6', bg:'#F4F6F9',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

const fmtAmt  = n => n ? '₹' + Number(n).toLocaleString('en-IN') : '₹0';
const today   = () => new Date().toISOString().split('T')[0];
const greet   = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; };
const extractArr = r => { const d = r?.data; return Array.isArray(d?.results) ? d.results : Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : []; };
const timeAgo = s => {
  if (!s) return '—';
  const diff = Date.now() - new Date(s).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = new Date(s); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
};

// Get YYYY-MM from a date string — handles both "2026-03-23" and ISO datetime
const getMonthKey = (dateStr) => {
  if (!dateStr) return null;
  // Take first 7 chars: "2026-03" from "2026-03-23" or "2026-03-23T..."
  const key = String(dateStr).slice(0, 7);
  // Validate: must be YYYY-MM format
  if (/^\d{4}-\d{2}$/.test(key)) return key;
  return null;
};

const STAGE_LABEL = {
  FOLLOW_UP:'Follow Up', LOGIN:'Login', SANCTION:'Sanction',
  DISBURSEMENT:'Disbursement', PDD:'PDD', REVENUE:'Revenue', COMPLETE:'Completed', CLOSED:'Closed',
};

const getStageLabel = (lead) => {
  const cs = lead?.current_stage;
  return (cs && STAGE_LABEL[cs]) ? STAGE_LABEL[cs] : 'Follow Up';
};

const isFollowUpStage  = (lead) => !lead?.current_stage || lead.current_stage === 'FOLLOW_UP';
const isClosedStage    = (lead) => lead?.current_stage === 'CLOSED' || lead?.current_stage === 'COMPLETE';
const isConvertedStage = (lead) => ['LOGIN','SANCTION','DISBURSEMENT','PDD','REVENUE','COMPLETE'].includes(lead?.current_stage);

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

const Sh = ({ h=20, w='100%', r=6, mb=0 }) => (
  <div style={{ height:h, width:w, borderRadius:r, marginBottom:mb, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'cfShimmer 1.4s infinite' }}/>
);

const Donut = ({ segs, size=100, thick=14, label, sub }) => {
  const r = (size - thick) / 2, c = 2 * Math.PI * r;
  const tot = segs.reduce((a, s) => a + (s.v || 0), 0);
  let off = 0;
  const arcs = segs.map(s => { const d = tot > 0 ? (s.v/tot)*c : 0, st=off; off+=d; return {...s, d, gap:c-d, off:c-st}; });
  return (
    <div style={{ position:'relative', width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform:'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={thick}/>
        {tot > 0 && arcs.map((a,i) => (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={a.c}
            strokeWidth={thick} strokeDasharray={`${a.d-1.5} ${a.gap+1.5}`} strokeDashoffset={a.off} strokeLinecap="round"/>
        ))}
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
        <div style={{ fontSize:18, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1 }}>{label}</div>
        {sub && <div style={{ fontSize:9.5, color:C.t4, fontWeight:600, marginTop:2, textTransform:'uppercase', letterSpacing:'0.05em' }}>{sub}</div>}
      </div>
    </div>
  );
};

/* BAR CHART — react-chartjs-2 (chart.js already installed) */
const Bars = ({ data, h=140 }) => {
  const chartData = {
    labels: data.map(d => d.l),
    datasets: [{
      label: 'Leads',
      data: data.map(d => d.v),
      backgroundColor: '#0D3B5E',
      hoverBackgroundColor: '#F5A623',
      borderRadius: 5,
      borderSkipped: false,
      maxBarThickness: 44,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 600 },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { title: items => items[0].label, label: ctx => ` ${ctx.parsed.y} lead${ctx.parsed.y !== 1 ? 's' : ''}` },
        backgroundColor: '#0D3B5E',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 9,
        cornerRadius: 7,
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#64748B', font: { size: 11 } },
      },
      y: {
        grid: { color: '#F1F5F9', lineWidth: 1 },
        border: { display: false },
        ticks: { color: '#94A3B8', font: { size: 11 }, precision: 0, stepSize: 1 },
        beginAtZero: true,
        min: 0,
      },
    },
  };
  return <div style={{ height:h, width:'100%' }}><Bar data={chartData} options={options}/></div>;
};

const KpiCard = ({ icon, iconBg, iconColor, accent, title, value, sub, subColor, loading, onClick }) => (
  <div onClick={onClick} style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'16px 18px', cursor:onClick?'pointer':'default', transition:'all 0.15s', position:'relative', overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,0.10)'; }}}
    onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 6px rgba(13,59,94,0.06)'; }}>
    <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:accent, borderRadius:'12px 12px 0 0' }}/>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
      <div style={{ width:38, height:38, borderRadius:10, background:iconBg, display:'flex', alignItems:'center', justifyContent:'center', color:iconColor, flexShrink:0 }}>{icon}</div>
      {onClick && <FiChevronRight size={14} style={{ color:C.t4, marginTop:2 }}/>}
    </div>
    {loading ? <Sh h={28} w="50%" mb={6}/> : <div style={{ fontSize:32, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1, marginBottom:5 }}>{value ?? 0}</div>}
    <div style={{ fontSize:12.5, fontWeight:600, color:C.t2 }}>{title}</div>
    {sub && !loading && <div style={{ fontSize:11.5, color:subColor||C.t4, marginTop:3, fontWeight:500 }}>{sub}</div>}
  </div>
);

const Card = ({ title, icon, badge, action, actionLabel, children, noPad=false, style={} }) => (
  <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)', ...style }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'13px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        {icon && <span style={{ color:C.navy, display:'flex', opacity:0.65 }}>{icon}</span>}
        <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>{title}</span>
        {badge != null && <span style={{ fontSize:11, fontWeight:700, color:C.navy, background:C.navyL, padding:'2px 8px', borderRadius:999, border:`1px solid rgba(13,59,94,0.12)` }}>{badge}</span>}
      </div>
      {action && (
        <button onClick={action} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, fontWeight:600, color:C.navy, background:'transparent', border:`1px solid ${C.border}`, borderRadius:7, padding:'5px 11px', cursor:'pointer', transition:'all 0.12s' }}
          onMouseEnter={e => { e.currentTarget.style.background=C.navyL; e.currentTarget.style.borderColor=C.navy; }}
          onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor=C.border; }}>
          {actionLabel} <FiArrowRight size={11}/>
        </button>
      )}
    </div>
    <div style={{ padding:noPad?0:18 }}>{children}</div>
  </div>
);

const PBar = ({ label, value, total, color, dotColor }) => {
  const pct = total > 0 ? Math.round((value/total)*100) : 0;
  return (
    <div style={{ marginBottom:13 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          <div style={{ width:7, height:7, borderRadius:'50%', background:dotColor||color, flexShrink:0 }}/>
          <span style={{ fontSize:12.5, fontWeight:500, color:C.t2 }}>{label}</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>{value}</span>
          <span style={{ fontSize:11, color:C.t4, minWidth:30, textAlign:'right', fontWeight:500 }}>{pct}%</span>
        </div>
      </div>
      <div style={{ height:5, background:'#F1F5F9', borderRadius:999, overflow:'hidden' }}>
        <div style={{ height:'100%', background:color, borderRadius:999, width:`${pct}%`, minWidth:pct>0?4:0, transition:'width 0.7s ease-out' }}/>
      </div>
    </div>
  );
};

const FollowRow = ({ lead, overdue, navigate }) => (
  <div onClick={() => navigate(`/leads/${lead.id}`)} style={{ display:'flex', alignItems:'center', gap:11, padding:'10px 0', borderBottom:`1px solid ${C.borderL}`, cursor:'pointer', borderRadius:6, transition:'background 0.08s' }}
    onMouseEnter={e => e.currentTarget.style.background='rgba(0,0,0,0.015)'}
    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
    <div style={{ width:32, height:32, borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13, background:overdue?C.redL:C.goldL, color:overdue?C.redB:C.gold, border:`1.5px solid ${overdue?C.redBord:'#F0D798'}` }}>
      {(lead.customer_name||'?')[0].toUpperCase()}
    </div>
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.t1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.customer_name}</div>
      <div style={{ fontSize:11.5, color:C.t3, marginTop:1 }}>{getStageLabel(lead)}</div>
    </div>
    <div style={{ textAlign:'right', flexShrink:0 }}>
      <div style={{ fontSize:12, fontWeight:overdue?700:400, color:overdue?C.redB:C.t3 }}>{fmtDate(lead.next_follow_up_date)}</div>
      {overdue && <div style={{ fontSize:10, fontWeight:700, color:C.redB, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:1 }}>OVERDUE</div>}
    </div>
  </div>
);

const ActRow = ({ icon, iBg, iCl, title, sub, time, isLast }) => (
  <div style={{ display:'flex', gap:11, alignItems:'flex-start', paddingBottom:isLast?0:13, marginBottom:isLast?0:13, borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
    <div style={{ width:32, height:32, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, background:iBg, color:iCl }}>{icon}</div>
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:13, fontWeight:600, color:C.t1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:2 }}>{title}</div>
      <div style={{ fontSize:12, color:C.t3 }}>{sub}</div>
    </div>
    <div style={{ fontSize:11.5, color:C.t4, whiteSpace:'nowrap', flexShrink:0 }}>{time}</div>
  </div>
);

const STAGE_BADGE_CFG = {
  FOLLOW_UP:{ bg:'#FFF7ED', color:'#C2410C' }, LOGIN:{ bg:'#EDE9FE', color:'#6D28D9' },
  SANCTION:{ bg:'#DBEAFE', color:'#1D4ED8' }, DISBURSEMENT:{ bg:'#DCFCE7', color:'#15803D' },
  PDD:{ bg:'#EBF4FF', color:'#0D3B5E' }, REVENUE:{ bg:'#CCFBF1', color:'#0F766E' },
  COMPLETE:{ bg:'#DCFCE7', color:'#15803D' },
  CLOSED:{ bg:'#F3F4F6', color:'#6B7280' },
};

const LeadRow = ({ lead, navigate, isLast }) => {
  const cs = lead?.current_stage || 'FOLLOW_UP';
  const td = today();
  const ov = cs === 'FOLLOW_UP' && lead.next_follow_up_date && lead.next_follow_up_date < td;
  const cfg = STAGE_BADGE_CFG[cs] || STAGE_BADGE_CFG.FOLLOW_UP;
  return (
    <tr onClick={() => navigate(`/leads/${lead.id}`)} style={{ cursor:'pointer', transition:'background 0.07s' }}
      onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
      onMouseLeave={e => e.currentTarget.style.background=C.white}>
      <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, verticalAlign:'middle' }}>
        <div style={{ fontWeight:700, fontSize:13, color:C.t1 }}>{lead.customer_name}</div>
        <div style={{ fontSize:11.5, color:C.t4, marginTop:1 }}>{lead.mobile_number}</div>
      </td>
      <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, verticalAlign:'middle', fontSize:12.5, color:C.t2 }}>{lead.product?.name||'—'}</td>
      <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, verticalAlign:'middle' }}>
        <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:999, fontSize:12, fontWeight:700, background:ov?C.redL:cfg.bg, color:ov?C.redB:cfg.color, whiteSpace:'nowrap' }}>
          {ov && <FiAlertCircle size={10}/>}{STAGE_LABEL[cs]||'Follow Up'}
        </span>
      </td>
      <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, verticalAlign:'middle', fontSize:12.5, whiteSpace:'nowrap', color:ov?C.redB:C.t2, fontWeight:ov?700:400 }}>{fmtDate(lead.next_follow_up_date)}</td>
      <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, verticalAlign:'middle', textAlign:'right' }}>
        <span style={{ fontSize:13, fontWeight:700, color:C.navy }}>{fmtAmt(lead.loan_amount)}</span>
      </td>
    </tr>
  );
};

/* ═══════════════════════════════════════════════════
   DASHBOARD COMPONENT
═══════════════════════════════════════════════════ */
export default function Dashboard() {
  const navigate = useNavigate();
  const user     = authService.getUser();
  const userName = authService.getUserDisplayName();
  const roleName = user?.role?.name || 'Super Admin';
  const canLeads = can('lead_read') || !user?.role;
  const canUsers = can('user_read') || !user?.role;
  const userLeadParam = {};

  const vw       = useWindowWidth();
  const isMobile = vw < 768;
  const isTablet = vw >= 768 && vw < 1024;

  const [leads,        setLeads]        = useState([]);
  const [users,        setUsers]        = useState([]);
  const [todayFULeads, setTodayFULeads] = useState([]);
  const [todayDisbExp, setTodayDisbExp] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [byMonth,      setByMonth]      = useState({});
  const [stats,        setStats]        = useState({
    total:0, followUp:0, login:0, sanction:0, disbursement:0,
    pdd:0, revenue:0, closed:0, overdue:0, todayFU:0, totalAmt:0, disburseAmt:0,
  });

  // ─── Period filter ───────────────────────────────────────────────
  const [period,      setPeriod]      = useState('all');  // 'today'|'week'|'month'|'custom'|'all'
  const [customFrom,  setCustomFrom]  = useState('');
  const [customTo,    setCustomTo]    = useState('');
  const [showCustom,  setShowCustom]  = useState(false);

  // Compute date range for selected period
  const getPeriodRange = () => {
    const now  = new Date();
    const pad  = n => String(n).padStart(2, '0');
    const fmt  = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (period === 'today') {
      const t = fmt(now);
      return { from: t, to: t };
    }
    if (period === 'week') {
      const day = now.getDay(); // 0=Sun
      const mon = new Date(now); mon.setDate(now.getDate() - ((day + 6) % 7));
      return { from: fmt(mon), to: fmt(now) };
    }
    if (period === 'month') {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: fmt(from), to: fmt(now) };
    }
    if (period === 'custom') {
      return { from: customFrom || '', to: customTo || '' };
    }
    return { from: '', to: '' }; // 'all'
  };

  // Filter leads by period
  const filterByPeriod = (allLeads) => {
    const { from, to } = getPeriodRange();
    if (!from && !to) return allLeads; // 'all'
    return allLeads.filter(l => {
      const dateStr = (l.lead_date || l.created_at || '').slice(0, 10);
      if (!dateStr) return false;
      if (from && dateStr < from) return false;
      if (to   && dateStr > to)   return false;
      return true;
    });
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const td = today();

      // Paginate all leads — backend default page=15, paginate until done
      const fetchAllLeads = async () => {
        const all = [];
        let page = 1;
        while (true) {
          const res = await api.getLeads({ page, page_size: 100, ...userLeadParam });
          const data = res?.data;
          const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
          all.push(...results);
          if (!data?.next || results.length === 0) break;
          page++;
          if (page > 20) break;
        }
        return all;
      };

      const [allLeads, uR, fuR, sanctR] = await Promise.all([
        canLeads ? fetchAllLeads() : [],
        canUsers ? api.getUsers({ page_size: 100 }) : null,
        canLeads ? api.getLeads({ next_follow_up_date: td, page_size: 100, ...userLeadParam }).catch(() => null) : null,
        canLeads ? api.getLeadSanctions({ expected_disbursement_date: td, page_size: 50, ...userLeadParam }).catch(() => null) : null,
      ]);

      const aL = Array.isArray(allLeads) ? allLeads : [];
      const aU = extractArr(uR);
      setLeads(aL);
      setUsers(aU);

      const fuArr = extractArr(fuR);
      setTodayFULeads(fuArr.length > 0 ? fuArr : aL.filter(l => l.next_follow_up_date === td).slice(0, 10));
      setTodayDisbExp(extractArr(sanctR));

      // Build monthly count — separate state so chart re-renders correctly
      const bm = {};
      let total=0, followUp=0, login=0, sanction=0, disbursement=0,
          pdd=0, revenue=0, closed=0, overdue=0, todayFU=0, totalAmt=0, disburseAmt=0;

      aL.forEach(l => {
        total++;
        totalAmt += Number(l.loan_amount) || 0;

        // Use lead_date first, then created_at — both give YYYY-MM-DD or ISO string
        const dateStr = l.lead_date || l.created_at || '';
        const mo = getMonthKey(dateStr);
        if (mo) bm[mo] = (bm[mo] || 0) + 1;

        const cs = l?.current_stage || 'FOLLOW_UP';
        switch (cs) {
          case 'FOLLOW_UP':
            followUp++;
            if (l.next_follow_up_date) {
              if (l.next_follow_up_date < td) overdue++;
              if (l.next_follow_up_date === td) todayFU++;
            }
            break;
          case 'LOGIN':        login++;         break;
          case 'SANCTION':     sanction++;      break;
          case 'DISBURSEMENT': disbursement++; disburseAmt += Number(l.loan_amount)||0; break;
          case 'PDD':          pdd++;           break;
          case 'REVENUE':      revenue++;       break;
          case 'CLOSED':       closed++;        break;
          default:             followUp++;      break;
        }
      });

      // Set separately so chart gets fresh reference
      setByMonth({...bm});
      setStats({ total, followUp, login, sanction, disbursement, pdd, revenue, closed, overdue, todayFU, totalAmt, disburseAmt });

    } catch (e) { console.error('Dashboard fetchAll error:', e); }
    finally { setLoading(false); }
  }, [canLeads, canUsers]); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const td = today();

  // ── Apply period filter to get filtered leads ──
  const filteredLeads = filterByPeriod(leads);

  // ── Recompute stats from filteredLeads ──
  const filteredStats = (() => {
    let total=0, followUp=0, login=0, sanction=0, disbursement=0,
        pdd=0, revenue=0, closed=0, overdue=0, todayFU=0, totalAmt=0, disburseAmt=0;
    filteredLeads.forEach(l => {
      total++;
      totalAmt += Number(l.loan_amount) || 0;
      const cs = l?.current_stage || 'FOLLOW_UP';
      switch (cs) {
        case 'FOLLOW_UP':
          followUp++;
          if (l.next_follow_up_date) {
            if (l.next_follow_up_date < td) overdue++;
            if (l.next_follow_up_date === td) todayFU++;
          }
          break;
        case 'LOGIN':        login++;        break;
        case 'SANCTION':     sanction++;     break;
        case 'DISBURSEMENT': disbursement++; disburseAmt += Number(l.loan_amount)||0; break;
        case 'PDD':          pdd++;          break;
        case 'REVENUE':      revenue++;      break;
        case 'CLOSED':       closed++;       break;
        default:             followUp++;     break;
      }
    });
    return { total, followUp, login, sanction, disbursement, pdd, revenue, closed, overdue, todayFU, totalAmt, disburseAmt };
  })();

  // Use filteredStats for all display
  const displayStats = filteredStats;

  const overdL = filteredLeads.filter(l => l.current_stage === 'FOLLOW_UP' && l.next_follow_up_date && l.next_follow_up_date < td).slice(0, 5);
  const todayL = filteredLeads.filter(l => l.next_follow_up_date === td).slice(0, 5);
  const upcomL = filteredLeads.filter(l => l.next_follow_up_date && l.next_follow_up_date > td)
    .sort((a, b) => a.next_follow_up_date.localeCompare(b.next_follow_up_date)).slice(0, 4);

  const recent = [...filteredLeads].sort((a, b) =>
    new Date(b.lead_date || b.created_at || 0) - new Date(a.lead_date || a.created_at || 0)
  ).slice(0, 8);

  const pipeline = displayStats.login + displayStats.sanction + displayStats.disbursement + displayStats.pdd + displayStats.revenue;
  const convRate = displayStats.total > 0 ? Math.round((displayStats.disbursement / displayStats.total) * 100) : 0;

  // Build 6-month chart data from byMonth state
  const now = new Date();
  const mData = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const label = d.toLocaleString('en-IN', { month: 'short' });
    return { l: label, v: byMonth[key] || 0 };
  });

  const dSegs = [
    { v:displayStats.followUp, c:C.goldB }, { v:displayStats.login, c:C.navy },
    { v:displayStats.sanction, c:C.skyB }, { v:displayStats.disbursement, c:C.greenB },
    { v:displayStats.closed, c:'#94A3B8' },
  ];
  const dLabels = ['Follow-up','Login','Sanction','Disburse','Closed'];

  // Period label for display
  const periodLabel = { today:'Today', week:'This Week', month:'This Month', custom:'Custom Range', all:'All Time' }[period];

  const actFeed = recent.slice(0,6).map((l,i) => {
    const ic = [
      { i:<FiUsers size={13}/>, bg:C.navyL, c:C.navy },
      { i:<FiPhone size={13}/>, bg:C.goldL, c:C.gold },
      { i:<FiCheckCircle size={13}/>, bg:C.greenL, c:C.greenB },
      { i:<FiFileText size={13}/>, bg:C.purpleL, c:C.purpleB },
      { i:<FiDollarSign size={13}/>, bg:C.skyL, c:C.skyB },
      { i:<FiActivity size={13}/>, bg:C.orangeL, c:C.orange },
    ][i % 6];
    return { icon:ic.i, iBg:ic.bg, iCl:ic.c, title:l.customer_name||'Lead', sub:`${getStageLabel(l)} · ${fmtAmt(l.loan_amount)}`, time:timeAgo(l.lead_date||l.created_at) };
  });

  const alerts = [];
  if (displayStats.overdue > 0) alerts.push({ type:'red',   icon:<FiAlertCircle size={13}/>, text:`${displayStats.overdue} follow-up${displayStats.overdue>1?'s':''} overdue — action needed` });
  if (displayStats.todayFU > 0) alerts.push({ type:'gold',  icon:<FiClock size={13}/>,       text:`${displayStats.todayFU} follow-up${displayStats.todayFU>1?'s':''} scheduled for today` });
  if (pipeline > 0)             alerts.push({ type:'green', icon:<FiTrendingUp size={13}/>,  text:`${pipeline} lead${pipeline>1?'s':''} currently in pipeline` });

  const g2 = isMobile?'1fr':'1fr 1fr';
  const g4 = isMobile?'repeat(2,1fr)':isTablet?'repeat(2,1fr)':'repeat(4,1fr)';
  const g5 = isMobile?'repeat(2,1fr)':isTablet?'repeat(3,1fr)':'repeat(5,1fr)';
  const g3 = isMobile?'1fr':isTablet?'1fr 1fr':'2.2fr 1fr 1.1fr';
  const gQ = isMobile?'1fr':isTablet?'repeat(2,1fr)':'repeat(3,1fr)';
  const gap = isMobile?10:12;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes cfShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .cf-dash { animation: cfFadeUp 0.25s ease both; }
      `}</style>

      <Layout pageTitle="Dashboard">
        <div className="cf-dash" style={{ display:'flex', flexDirection:'column', gap, fontFamily:"'Inter',sans-serif", paddingBottom:28 }}>

          {/* HERO */}
          <div style={{ borderRadius:14, overflow:'hidden', background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(0,0,0,0.18)', position:'relative' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ padding:isMobile?'14px':'20px 28px', position:'relative', zIndex:1 }}>
              <div style={{ marginBottom:isMobile?12:16 }}>
                <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.48)', marginBottom:3 }}>{greet()},</div>
                <div style={{ fontSize:isMobile?20:22, fontWeight:800, color:'#fff', letterSpacing:'-0.03em', lineHeight:1.15, marginBottom:10 }}>{userName}</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 11px', borderRadius:999, fontSize:11.5, fontWeight:700, background:'rgba(245,166,35,0.18)', border:'1px solid rgba(245,166,35,0.35)', color:'#FBBF4A' }}>
                    <FiShield size={10}/> {roleName}
                  </span>
                  {stats.overdue > 0 && (
                    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 11px', borderRadius:999, fontSize:11.5, fontWeight:700, background:'rgba(220,38,38,0.20)', border:'1px solid rgba(220,38,38,0.35)', color:'#FCA5A5' }}>
                      <FiAlertCircle size={10}/> {stats.overdue} overdue
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)', gap:8 }}>
                {[
                  { lbl:"Today's F/U", val:loading?'–':displayStats.todayFU,              icon:<FiClock size={13}/>,      ac:'rgba(245,166,35,0.90)' },
                  { lbl:'Total Leads', val:loading?'–':displayStats.total,               icon:<FiUsers size={13}/>,      ac:'rgba(255,255,255,0.60)' },
                  { lbl:'Disbursed',   val:loading?'–':fmtAmt(displayStats.disburseAmt), icon:<span style={{fontSize:13,fontWeight:800,color:'#4ADE80'}}>₹</span>, ac:'#4ADE80' },
                  { lbl:'Conv. Rate',  val:loading?'–':`${convRate}%`,            icon:<FiTarget size={13}/>,     ac:'#60A5FA' },
                ].map(it => (
                  <div key={it.lbl} style={{ borderRadius:10, padding:isMobile?'10px 12px':'12px 16px', textAlign:'center', background:'rgba(255,255,255,0.09)', border:'1px solid rgba(255,255,255,0.14)' }}>
                    <div style={{ display:'flex', justifyContent:'center', marginBottom:4, color:it.ac }}>{it.icon}</div>
                    <div style={{ fontSize:isMobile?17:20, fontWeight:800, color:'#fff', lineHeight:1, letterSpacing:'-0.04em' }}>{it.val}</div>
                    <div style={{ fontSize:9.5, fontWeight:600, marginTop:4, color:'rgba(255,255,255,0.48)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{it.lbl}</div>
                  </div>
                ))}
              </div>
              {!isMobile && (
                <button onClick={fetchAll} title="Refresh" style={{ position:'absolute', top:20, right:20, width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:9, background:'rgba(255,255,255,0.09)', border:'1px solid rgba(255,255,255,0.16)', color:'rgba(255,255,255,0.60)', cursor:'pointer' }}>
                  <FiRefreshCw size={13}/>
                </button>
              )}
            </div>
          </div>

          {/* PERIOD FILTER BAR */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'14px 18px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <FiCalendar size={16} style={{ color:C.navy, opacity:0.7, flexShrink:0 }}/>
            <span style={{ fontSize:16, fontWeight:700, color:C.t2, marginRight:4 }}>Period:</span>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', flex:1 }}>
              {[['all','All Time'],['today','Today'],['week','This Week'],['month','This Month'],['custom','Custom']].map(([val, lbl]) => (
                <button key={val} onClick={() => { setPeriod(val); setShowCustom(val === 'custom'); }}
                  style={{ padding:'8px 18px', borderRadius:20, border:`1.5px solid ${period===val ? C.navy : C.border}`, background: period===val ? C.navy : C.white, color: period===val ? '#fff' : C.t2, fontSize:16, fontWeight: period===val ? 700 : 500, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s', whiteSpace:'nowrap' }}>
                  {lbl}
                </button>
              ))}
            </div>
            {!loading && (
              <span style={{ fontSize:16, color:C.t4, fontWeight:600, whiteSpace:'nowrap' }}>
                {displayStats.total} lead{displayStats.total !== 1 ? 's' : ''} · {periodLabel}
              </span>
            )}
          </div>

          {/* Custom date inputs - shown below when Custom is selected */}
          {period === 'custom' && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'12px 18px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <span style={{ fontSize:16, color:C.t4, flexShrink:0 }}>From:</span>
              <DateInput value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                style={{ height:40, padding:'0 10px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:16, color:C.t1, fontFamily:'inherit', outline:'none', width:150 }}/>
              <span style={{ fontSize:16, color:C.t4, flexShrink:0 }}>To:</span>
              <DateInput value={customTo} onChange={e => setCustomTo(e.target.value)}
                style={{ height:40, padding:'0 10px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:16, color:C.t1, fontFamily:'inherit', outline:'none', width:150 }}/>
            </div>
          )}

          {/* ALERTS */}
          {!loading && alerts.length > 0 && (
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {alerts.map((a,i) => {
                const cfg = { red:{bg:C.redL,border:C.redBord,color:C.redB}, gold:{bg:C.goldL,border:'#F0D798',color:C.gold}, green:{bg:C.greenL,border:'#BBF7D0',color:C.greenB} }[a.type];
                const clickFn = a.type === 'red' ? () => navigate('/leads?filter=overdue') : a.type === 'gold' ? () => navigate('/leads?filter=today_followup') : undefined;
                return <div key={i} onClick={clickFn} style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 14px', borderRadius:9, background:cfg.bg, border:`1px solid ${cfg.border}`, fontSize:12.5, color:cfg.color, fontWeight:600, flex:1, minWidth:160, cursor:clickFn?'pointer':'default' }}><span style={{ flexShrink:0 }}>{a.icon}</span>{a.text}</div>;
              })}
            </div>
          )}

          {/* KPI */}
          <div style={{ display:'grid', gridTemplateColumns:g4, gap }}>
            <KpiCard title="Total Leads"  value={displayStats.total}    sub={periodLabel}              icon={<FiUsers size={17}/>}      iconBg={C.navyL}   iconColor={C.navy}   accent={C.navy}   loading={loading} onClick={() => navigate('/leads')}/>
            <KpiCard title="Follow-ups"   value={displayStats.followUp} sub={displayStats.overdue>0?`⚠ ${displayStats.overdue} overdue`:'Active'} subColor={displayStats.overdue>0?C.redB:C.t4} icon={<FiPhone size={17}/>} iconBg={C.goldL} iconColor={C.gold} accent={C.goldB} loading={loading} onClick={() => navigate('/leads?stage=FOLLOW_UP')}/>
            <KpiCard title="In Pipeline"  value={pipeline}       sub="Login · Sanction · Disburse · PDD · Revenue"           icon={<FiTrendingUp size={17}/>} iconBg={C.greenL} iconColor={C.greenB} accent={C.greenB} loading={loading} onClick={() => navigate('/leads?stage=pipeline')}/>
            <KpiCard title="Closed"       value={displayStats.closed}   sub={periodLabel}              icon={<FiXCircle size={17}/>}    iconBg="#F1F5F9"   iconColor="#64748B"  accent="#94A3B8"  loading={loading} onClick={() => navigate('/leads?stage=CLOSED')}/>
          </div>

          {/* PIPELINE STRIP */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:isMobile?'14px':'16px 20px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.10em', marginBottom:12 }}>Pipeline Stages</div>
            <div style={{ display:'grid', gridTemplateColumns:g5, gap:isMobile?8:10 }}>
              {[
                { lbl:'Login',        val:displayStats.login,        color:C.navy,    bg:C.navyL,   stage:'LOGIN' },
                { lbl:'Sanction',     val:displayStats.sanction,     color:C.skyB,    bg:C.skyL,    stage:'SANCTION' },
                { lbl:'Disbursement', val:displayStats.disbursement, color:C.greenB,  bg:C.greenL,  stage:'DISBURSEMENT' },
                { lbl:'PDD',          val:displayStats.pdd,          color:C.gold,    bg:C.goldL,   stage:'PDD' },
                { lbl:'Revenue',      val:displayStats.revenue,      color:C.purpleB, bg:C.purpleL, stage:'REVENUE' },
              ].map(s => (
                <div key={s.lbl} onClick={() => navigate(`/leads?stage=${s.stage}`)} style={{ background:s.bg, borderRadius:10, padding:isMobile?'10px':'16px 18px', borderTop:`3px solid ${s.color}`, transition:'transform 0.15s,box-shadow 0.15s', cursor:'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 16px rgba(13,59,94,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                  <div style={{ fontSize:10, fontWeight:700, color:C.t2, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>{s.lbl}</div>
                  {loading?<Sh h={20} w={40}/>:<div style={{ fontSize:isMobile?16:20, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1 }}>{s.val}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* CHARTS */}
          <div style={{ display:'grid', gridTemplateColumns:g3, gap }}>
            <Card title="Monthly Lead Trend" icon={<FiBarChart2 size={13}/>}>
              {loading ? <Sh h={160}/> : <>
                <Bars data={mData} h={160}/>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, paddingTop:12, borderTop:`1px solid ${C.borderL}` }}>
                  <span style={{ fontSize:12, color:C.t4, fontWeight:500 }}>6-month total</span>
                  <span style={{ fontSize:16, fontWeight:800, color:C.navy, letterSpacing:'-0.03em' }}>{mData.reduce((a,d)=>a+d.v,0)}</span>
                </div>
              </>}
            </Card>

            <Card title="Status Mix" icon={<FiActivity size={13}/>}>
              {loading ? <Sh h={200}/> :
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14 }}>
                  <Donut segs={dSegs.filter(s=>s.v>0)} size={100} thick={14} label={stats.total} sub="total"/>
                  <div style={{ width:'100%', display:'flex', flexDirection:'column', gap:7 }}>
                    {dSegs.map((s,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                          <div style={{ width:7, height:7, borderRadius:'50%', background:s.c, flexShrink:0 }}/>
                          <span style={{ fontSize:12, fontWeight:500, color:C.t2 }}>{dLabels[i]}</span>
                        </div>
                        <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>{s.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              }
            </Card>

            <Card title="Pipeline Health" icon={<FiTrendingUp size={13}/>}>
              {loading ? [0,1,2,3,4].map(i=><Sh key={i} h={18} mb={13}/>) : <>
                <PBar label="Follow-up"    value={displayStats.followUp}    total={displayStats.total} color={C.goldB}  dotColor={C.gold}/>
                <PBar label="Login"        value={displayStats.login}        total={displayStats.total} color={C.navy}/>
                <PBar label="Sanction"     value={displayStats.sanction}     total={displayStats.total} color={C.skyB}/>
                <PBar label="Disbursement" value={displayStats.disbursement} total={displayStats.total} color={C.greenB}/>
                <PBar label="Closed"       value={displayStats.closed}       total={displayStats.total} color="#94A3B8"/>
                {canUsers && (
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, paddingTop:12, borderTop:`1px solid ${C.borderL}` }}>
                    <span style={{ fontSize:12, color:C.t3, fontWeight:500 }}>System Users</span>
                    <span style={{ fontSize:16, fontWeight:800, color:C.navy }}>{users.length}</span>
                  </div>
                )}
              </>}
            </Card>
          </div>

          {/* TODAY */}
          <div style={{ display:'grid', gridTemplateColumns:g2, gap }}>
            <Card title="Today's Follow-ups" icon={<FiClock size={13}/>} badge={!loading?(todayFULeads.length||stats.todayFU):null} action={()=>navigate('/leads')} actionLabel="View all">
              {loading ? [0,1,2,3].map(i=><Sh key={i} h={44} mb={12}/>) : (
                todayFULeads.length === 0 && stats.todayFU === 0
                  ? <div style={{ textAlign:'center', padding:'28px 0', fontSize:13, color:C.t4 }}>No follow-ups scheduled for today</div>
                  : (todayFULeads.length>0?todayFULeads:leads.filter(l=>l.next_follow_up_date===today())).slice(0,6).map((l,i)=><FollowRow key={l.id||i} lead={l} overdue={false} navigate={navigate}/>)
              )}
            </Card>

            <Card title="Expected Disbursements Today" icon={<span style={{fontSize:13,fontWeight:700}}>₹</span>} badge={!loading?todayDisbExp.length:null} action={()=>navigate('/leads')} actionLabel="View all">
              {loading ? [0,1,2].map(i=><Sh key={i} h={52} mb={12}/>) : (
                todayDisbExp.length === 0
                  ? <div style={{ textAlign:'center', padding:'28px 0', fontSize:13, color:C.t4 }}>No disbursements expected today</div>
                  : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {todayDisbExp.slice(0,5).map((sr,i) => (
                        <div key={sr.id||i} onClick={()=>sr.lead&&navigate(`/leads/${sr.lead}`)} style={{ display:'flex', alignItems:'center', gap:11, padding:'10px 0', borderBottom:`1px solid ${C.borderL}`, cursor:sr.lead?'pointer':'default' }}>
                          <div style={{ width:32, height:32, borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13, background:C.greenL, color:C.greenB, border:'1.5px solid #86EFAC' }}>₹</div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:C.t1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sr.lead_detail?.customer_name||`Lead #${sr.lead}`}</div>
                            <div style={{ fontSize:11.5, color:C.t3, marginTop:1 }}>Sanctioned: {sr.sanctioned_amount?'₹'+Number(sr.sanctioned_amount).toLocaleString('en-IN'):'—'}</div>
                          </div>
                          <div style={{ fontSize:12, fontWeight:600, color:C.greenB, flexShrink:0 }}>{fmtDate(sr.expected_disbursement_date)}</div>
                        </div>
                      ))}
                    </div>
              )}
            </Card>
          </div>

          {/* ACTIVITY + FOLLOWUPS */}
          <div style={{ display:'grid', gridTemplateColumns:g2, gap }}>
            <Card title="Recent Activity" icon={<FiActivity size={13}/>} badge={actFeed.length} action={()=>navigate('/leads')} actionLabel="View all">
              {loading ? [0,1,2,3].map(i=><Sh key={i} h={40} mb={13}/>) :
                actFeed.length===0
                  ? <div style={{ textAlign:'center', padding:'28px 0', fontSize:13, color:C.t4 }}>No recent activity</div>
                  : actFeed.map((a,i)=><ActRow key={i} {...a} isLast={i===actFeed.length-1}/>)
              }
            </Card>

            <Card title="Follow-ups" icon={<FiCalendar size={13}/>} action={()=>navigate('/leads')} actionLabel="View all">
              {loading && [0,1,2,3].map(i=><Sh key={i} h={44} mb={12}/>)}
              {!loading && (<>
                {overdL.length>0 && <div style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:10.5, fontWeight:800, color:C.redB, textTransform:'uppercase', letterSpacing:'0.10em', marginBottom:8 }}>
                    <FiAlertCircle size={11}/> Overdue
                    <span style={{ background:C.redL, color:C.redB, padding:'1px 7px', borderRadius:999, border:`1px solid ${C.redBord}` }}>{overdL.length}</span>
                  </div>
                  {overdL.map((l,i)=><FollowRow key={i} lead={l} overdue navigate={navigate}/>)}
                </div>}
                {todayL.length>0 && <div style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:10.5, fontWeight:800, color:'#92670C', textTransform:'uppercase', letterSpacing:'0.10em', marginBottom:8 }}>
                    <FiClock size={11}/> Today
                    <span style={{ background:C.goldL, color:'#92670C', padding:'1px 7px', borderRadius:999 }}>{todayL.length}</span>
                  </div>
                  {todayL.map((l,i)=><FollowRow key={i} lead={l} overdue={false} navigate={navigate}/>)}
                </div>}
                {upcomL.length>0 && <div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:10.5, fontWeight:800, color:C.navy, textTransform:'uppercase', letterSpacing:'0.10em', marginBottom:8 }}>
                    <FiCalendar size={11}/> Upcoming
                  </div>
                  {upcomL.map((l,i)=><FollowRow key={i} lead={l} overdue={false} navigate={navigate}/>)}
                </div>}
                {!overdL.length&&!todayL.length&&!upcomL.length && (
                  <div style={{ textAlign:'center', padding:'28px 0', fontSize:13, color:C.t4 }}>No follow-ups scheduled</div>
                )}
              </>)}
            </Card>
          </div>

          {/* QUICK STATS */}
          <div style={{ display:'grid', gridTemplateColumns:gQ, gap }}>
            <Card title="Conversion Funnel" icon={<FiZap size={13}/>}>
              {loading ? <Sh h={120}/> : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {[
                    { lbl:'Total Leads', val:displayStats.total,        pct:100,  color:C.navy  },
                    { lbl:'Follow-up',   val:displayStats.followUp,     pct:displayStats.total>0?Math.round(displayStats.followUp/displayStats.total*100):0,     color:C.goldB  },
                    { lbl:'In Pipeline', val:pipeline,           pct:displayStats.total>0?Math.round(pipeline/displayStats.total*100):0,           color:C.skyB   },
                    { lbl:'Disbursed',   val:displayStats.disbursement, pct:displayStats.total>0?Math.round(displayStats.disbursement/displayStats.total*100):0, color:C.greenB },
                  ].map((row,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:80, fontSize:12, color:C.t3, fontWeight:500, flexShrink:0 }}>{row.lbl}</div>
                      <div style={{ flex:1, height:6, background:'#F1F5F9', borderRadius:999, overflow:'hidden' }}>
                        <div style={{ height:'100%', background:row.color, borderRadius:999, width:`${row.pct}%`, transition:'width 0.7s ease-out' }}/>
                      </div>
                      <div style={{ width:28, fontSize:12, fontWeight:700, color:C.t1, textAlign:'right', flexShrink:0 }}>{row.val}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Loan Portfolio" icon={<span style={{fontSize:13,fontWeight:700}}>₹</span>}>
              {loading ? <Sh h={120}/> :
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {[
                    { lbl:'Total Applied',   val:fmtAmt(displayStats.totalAmt),    icon:<FiFileText size={13}/>,    bg:C.navyL,  color:C.navy   },
                    { lbl:'Total Disbursed', val:fmtAmt(displayStats.disburseAmt), icon:<FiCheckCircle size={13}/>, bg:C.greenL, color:C.greenB },
                    { lbl:'Conversion Rate', val:`${convRate}%`,            icon:<FiAward size={13}/>,       bg:C.goldL,  color:C.gold   },
                  ].map((it,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:9, background:it.bg }}>
                      <div style={{ width:28, height:28, borderRadius:7, background:'rgba(255,255,255,0.65)', display:'flex', alignItems:'center', justifyContent:'center', color:it.color, flexShrink:0 }}>{it.icon}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, color:C.t3, fontWeight:500 }}>{it.lbl}</div>
                        <div style={{ fontSize:14, fontWeight:800, color:C.t1, letterSpacing:'-0.02em' }}>{it.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              }
            </Card>

            <Card title="Team Overview" icon={<FiUserCheck size={13}/>}>
              {loading ? <Sh h={120}/> :
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {[
                    { lbl:'System Users', val:users.length,               icon:<FiUsers size={13}/>,    bg:C.navyL,   color:C.navy    },
                    { lbl:'Active Leads', val:displayStats.total-displayStats.closed,   icon:<FiActivity size={13}/>, bg:C.skyL,    color:C.skyB    },
                    { lbl:'Leads Closed', val:displayStats.closed,               icon:<FiXCircle size={13}/>,  bg:'#F1F5F9', color:'#64748B' },
                  ].map((it,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:9, background:it.bg }}>
                      <div style={{ width:28, height:28, borderRadius:7, background:'rgba(255,255,255,0.65)', display:'flex', alignItems:'center', justifyContent:'center', color:it.color, flexShrink:0 }}>{it.icon}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, color:C.t3, fontWeight:500 }}>{it.lbl}</div>
                        <div style={{ fontSize:14, fontWeight:800, color:C.t1 }}>{it.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              }
            </Card>
          </div>

          {/* RECENT LEADS */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:isMobile?'10px 14px':'13px 20px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC' }}>
              <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                <FiUserCheck size={14} style={{ color:C.navy, opacity:0.65 }}/>
                <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>Recent Leads</span>
                {!loading && <span style={{ fontSize:11, fontWeight:700, color:C.navy, background:C.navyL, padding:'2px 8px', borderRadius:999, border:'1px solid rgba(13,59,94,0.12)' }}>{recent.length}</span>}
                {!loading && period !== 'all' && <span style={{ fontSize:11, fontWeight:600, color:C.t4, background:C.borderL, padding:'2px 8px', borderRadius:999 }}>{periodLabel}</span>}
              </div>
              <button onClick={()=>navigate('/leads')} style={{ display:'flex', alignItems:'center', gap:6, fontSize:isMobile?11.5:12.5, fontWeight:700, color:'#fff', background:C.navy, border:'none', borderRadius:8, padding:isMobile?'6px 10px':'7px 14px', cursor:'pointer', transition:'all 0.12s', boxShadow:'0 2px 8px rgba(13,59,94,0.25)' }}
                onMouseEnter={e=>e.currentTarget.style.background=C.navyD}
                onMouseLeave={e=>e.currentTarget.style.background=C.navy}>
                <FiEye size={13}/> {isMobile?'All':'View All Leads'}
              </button>
            </div>
            {loading ? (
              <div style={{ padding:16, display:'flex', flexDirection:'column', gap:10 }}>
                {[0,1,2,3,4].map(i=><Sh key={i} h={44}/>)}
              </div>
            ) : (
              <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC' }}>
                      {['Customer','Product','Status','Follow-up','Amount'].map((h,i) => (
                        <th key={h} style={{ textAlign:i===4?'right':'left', padding:'10px 14px', fontSize:11, fontWeight:700, color:'#1F2937', textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:`1.5px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recent.length===0
                      ? <tr><td colSpan={5} style={{ padding:'36px 16px', textAlign:'center', fontSize:13, color:C.t4 }}>No leads found</td></tr>
                      : recent.map((l,i)=><LeadRow key={l.id} lead={l} navigate={navigate} isLast={i===recent.length-1}/>)
                    }
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </Layout>
    </>
  );
}
